import React from 'react';
import { Modal } from '../../../CommonComponents/OPL/Modal/Modal';
import { ModalBody } from '../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalCloseButton } from '../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalHeader } from '../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalFooter } from '../../../CommonComponents/OPL/Modal/ModalFooter';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { useShareWithThirdPartyMutation } from '../../api/useShareWithThirdPartyMutation';
import { useRevokeThirdPartyShareMutation } from '../../api/useRevokeThirdPartyShareMutation';
import { useRouteParams } from '../../hooks/useRouteParams';
import { Alert } from '../../../CommonComponents/OPL/Alert/Alert';
import { EntityDTO, EntityTypeDTO } from '../../DocumentVault.meta';

interface Props {
  entity: EntityDTO;
  onRequestClose(): void;
}

export const ShareRevokeThirdPartyEntityModal: React.FC<Props> = ({ entity, onRequestClose }) => {
  const { clientId } = useRouteParams();

  const isRevoke = entity.isSharedWithThirdParty === true;
  const entityTypeLabel = entity.type === EntityTypeDTO.Folder ? 'folder' : 'file';

  const {
    mutateAsync: shareWithThirdParty,
    isLoading: isSharingWithThirdParty,
    error: shareError,
  } = useShareWithThirdPartyMutation();
  const {
    mutateAsync: revokeThirdPartyShare,
    isLoading: isRevokingThirdPartyShare,
    error: revokeError,
  } = useRevokeThirdPartyShareMutation();

  const isLoading = isSharingWithThirdParty || isRevokingThirdPartyShare;
  const error = shareError || revokeError;

  const onConfirm = async () => {
    const mutationParams = {
      clientId,
      path: entity.path,
      providerType: entity.providerType,
    };

    try {
      if (isRevoke) {
        await revokeThirdPartyShare(mutationParams);
      } else {
        await shareWithThirdParty(mutationParams);
      }

      onRequestClose();
    } catch {
      // Ignore
    }
  };

  return (
    <Modal isOpen onRequestClose={onRequestClose} shouldCloseOnOverlayClick={false} size="medium">
      <ModalHeader variant="warning">
        {isRevoke ? 'Revoke Third Party Access' : 'Share with Third Party'}
      </ModalHeader>
      <ModalCloseButton onClick={onRequestClose} disabled={isLoading} />

      {error && <Alert message={error.message} variant="error" mx="24" mt="0" mb="16" />}

      <ModalBody>
        {isRevoke ? (
          <>
            This {entityTypeLabel} is currently being shared with document vault only third-party
            users.{' '}
            <Text as="span" fontWeight={500}>
              Would you like to revoke access to the {entityTypeLabel} &quot;{entity.name}&quot;?
            </Text>
          </>
        ) : (
          <>
            You are about to share this {entityTypeLabel} with document vault only access
            third-party users.{' '}
            <Text as="span" fontWeight={500}>
              Are you sure you want to share the {entityTypeLabel} &quot;{entity.name}&quot;?
            </Text>
          </>
        )}
      </ModalBody>

      <ModalFooter>
        <Button variant="subtleInactive" onClick={onRequestClose} disabled={isLoading}>
          Cancel
        </Button>

        <Button
          variant={isRevoke ? 'error' : 'primary'}
          isLoading={isLoading}
          onClick={onConfirm}
          ml="12"
        >
          {isRevoke ? 'Revoke' : 'Share'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
